import React from 'react'
import { connect } from 'react-redux'
import { CSSTransition } from 'react-transition-group'

import { RootStateTypeExtra } from '@store/types'

import PropertiesListSkeleton from '@components/compare-property/skeleton'
import usePropertyCompareFirebaseTrigger from '@components/compare-property/utils'
import DataHandler, { Status } from '@components/data-handler/data-handler'

import { useGetBuildingQuerySilent } from '@api/building'

import { useCompareProperty } from '@adUtilities/components/compare-property/utils'
import { PropertyType, Unit } from '@adUtilities/types'

import PropertiesList, { Property } from './properties-list'

interface AddActionMode {
  actionMode: 'add'
  replaceIndex?: number
}

interface ReplaceActionMode {
  actionMode: 'replace'
  replaceIndex: number
}

type UnitsListProps = {
  className?: string
  containerClassName?: string
  projectId: string
  comparePropertyProps: ReturnType<typeof useCompareProperty>
  onActionComplete?: () => void
  selectedBlock?: string
} & (AddActionMode | ReplaceActionMode)

function UnitsList({
  className = '',
  containerClassName = '',
  projectId,
  comparePropertyProps,
  actionMode,
  replaceIndex,
  onActionComplete,
  selectedBlock,
}: UnitsListProps) {
  const {
    checkPropertyComparability,
    checkPropertyPositionInCompareMode,
    validateAdd,
    validateReplace,
  } = comparePropertyProps
  const buildingQueryResponse = useGetBuildingQuerySilent({
    projectName: projectId,
  })

  const { triggerComparisonAdd, triggerComparisonReplace } =
    usePropertyCompareFirebaseTrigger({
      comparePropertyProps,
    })

  const properties = React.useMemo<Unit[]>(() => {
    if (buildingQueryResponse.status !== Status.FULFILLED) {
      return []
    }
    const levels =
      buildingQueryResponse.levels?.flatMap((item) => item.data) || []
    return selectedBlock
      ? levels.filter((item) => item.blockId === selectedBlock)
      : levels
  }, [buildingQueryResponse.status, selectedBlock])

  const validateAction = React.useCallback(
    (data: Unit) => {
      if (
        !checkPropertyComparability({
          data,
          propertyType: PropertyType.Unit,
        })
      ) {
        return false
      }
      if (actionMode === 'add') {
        const { validated } = validateAdd({
          property: {
            data,
            propertyType: PropertyType.Unit,
            references: {
              levelId: data.metas.level,
            },
          },
        })
        return validated
      }
      if (actionMode === 'replace' && replaceIndex !== undefined) {
        const { validated } = validateReplace({
          property: {
            data,
            propertyType: PropertyType.Unit,
            references: {
              levelId: data.metas.level,
            },
          },
          index: replaceIndex,
        })
        return validated
      }
      return false
    },
    [actionMode, validateAdd, validateReplace, replaceIndex]
  )

  const propertiesListProp = React.useMemo<Property[]>(
    () =>
      properties.map<Property>((item) => ({
        id: item.id,
        name: item.name,
        price: item.metas.price,
        status: item.metas.status,
        canAction: validateAction(item),
        hidePrice: item.metas.hidePrice,
        selected: !!checkPropertyPositionInCompareMode(item.id),
      })),
    [properties, validateAction, checkPropertyPositionInCompareMode]
  )

  const handleAdd = React.useCallback(
    (property: Unit) => {
      const propertyPositionInCompareMode = checkPropertyPositionInCompareMode(
        property.id
      )
      if (propertyPositionInCompareMode) {
        return
      }
      triggerComparisonAdd({
        property: {
          propertyType: PropertyType.Unit,
          data: property,
          references: {
            levelId: property.metas.level,
          },
        },
      })
      onActionComplete?.()
    },
    [checkPropertyPositionInCompareMode, triggerComparisonAdd, onActionComplete]
  )

  const handleReplace = React.useCallback(
    (property: Unit) => {
      const propertyPositionInCompareMode = checkPropertyPositionInCompareMode(
        property.id
      )
      if (
        propertyPositionInCompareMode ||
        actionMode !== 'replace' ||
        replaceIndex === undefined
      ) {
        return
      }
      triggerComparisonReplace({
        property: {
          propertyType: PropertyType.Unit,
          data: property,
          references: {
            levelId: property.metas.level,
          },
        },
        index: replaceIndex,
      })
      onActionComplete?.()
    },
    [
      checkPropertyPositionInCompareMode,
      triggerComparisonReplace,
      actionMode,
      replaceIndex,
      onActionComplete,
    ]
  )

  const handleClick = React.useCallback(
    (propertyId: string) => {
      const matchedProperty = properties.find((item) => item.id === propertyId)
      if (!matchedProperty) {
        return
      }

      if (actionMode === 'add') {
        handleAdd(matchedProperty)
      }

      if (actionMode === 'replace') {
        handleReplace(matchedProperty)
      }
    },
    [properties, actionMode, handleAdd, handleReplace]
  )

  return (
    <CSSTransition
      classNames="transition-property-compare-toggle"
      timeout={300}
      in
      appear
      mountOnEnter
      unmountOnExit
    >
      <div className={`h-full w-full ${className}`}>
        <div
          className={`custom-scrollbar h-full w-full overflow-y-auto ${containerClassName}`}
        >
          <DataHandler
            payloads={{
              ...buildingQueryResponse,
              data: buildingQueryResponse.levels,
            }}
            skeletonFrame={<PropertiesListSkeleton />}
          >
            <PropertiesList data={propertiesListProp} onClick={handleClick} />
          </DataHandler>
        </div>
      </div>
    </CSSTransition>
  )
}

export default connect(({ projectIdentity }: RootStateTypeExtra) => ({
  projectId: projectIdentity.projectId,
}))(UnitsList)
